import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchUsage, fetchMySpace, fetchFolders, fetchItems, requestStorage } from '../services/api';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { useAppStore } from '../store/useAppStore';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function itemBytes(item: { currentVersion?: { size_bytes?: number }; size_bytes?: string | number | null }): number {
  return Number(item.currentVersion?.size_bytes ?? item.size_bytes ?? 0);
}

export default function Storage() {
  const { toast, isAdmin, viewMode } = useAppStore();
  const navigate = useNavigate();
  const [showRequest, setShowRequest] = useState(false);
  const [requestGB, setRequestGB] = useState(10);
  const [requestReason, setRequestReason] = useState('');

  const { data: usage, isLoading } = useQuery({
    queryKey: qk.usage(),
    queryFn: fetchUsage,
  });

  const { data: mySpace } = useQuery({ queryKey: ['my-space'], queryFn: fetchMySpace });
  const spaceId = mySpace?.id;

  const { data: folders = [] } = useQuery({
    queryKey: qk.folders(spaceId ?? '', undefined),
    queryFn: () => fetchFolders(spaceId!),
    enabled: !!spaceId,
  });

  const { data: fileRows = [] } = useQuery({
    queryKey: ['storage-breakdown', spaceId, folders.map(f => f.id).join(',')],
    queryFn: async () => {
      const rootItems = await fetchItems(spaceId!);
      const rootBytes = rootItems.reduce((s, i) => s + itemBytes(i), 0);
      const folderRows = await Promise.all(folders.map(async f => {
        const items = await fetchItems(spaceId!, f.id);
        return { id: f.id, name: f.name, bytes: items.reduce((s, i) => s + itemBytes(i), 0), isRoot: false };
      }));
      return [...folderRows, { id: '__root__', name: 'My Drive', bytes: rootBytes, isRoot: true }].sort((a, b) => b.bytes - a.bytes);
    },
    enabled: !!spaceId,
  });

  const requestMutation = useMutation({
    mutationFn: () => requestStorage(requestGB * 1024 * 1024 * 1024, requestReason),
    onSuccess: () => { toast('Storage request submitted'); setShowRequest(false); setRequestReason(''); },
  });

  const usedBytes = usage?.used_bytes ?? 0;
  const quotaBytes = usage?.quota_bytes ?? (50 * 1024 * 1024 * 1024); // 50GB default
  const pct = quotaBytes > 0 ? Math.min(100, (usedBytes / quotaBytes) * 100) : 0;
  const danger = pct > 90;
  const availableBytes = Math.max(0, quotaBytes - usedBytes);
  const maxRowBytes = Math.max(1, ...fileRows.map(r => r.bytes));

  if (isLoading) {
    return (
      <div style={{ flex: 1, padding: 24 }}>
        <div className="t-skeleton" style={{ width: 200, height: 28, borderRadius: 6, marginBottom: 20 }} />
        <div className="t-skeleton" style={{ height: 200, borderRadius: 10 }} />
      </div>
    );
  }

  // Ring gauge (SVG-based)
  const R = 68;
  const C = 2 * Math.PI * R;
  const filled = (pct / 100) * C;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <div style={{ padding: '24px 24px 0' }}>
        <h2 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700 }}>Storage</h2>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 4 }}>What you're using across SK Drive.</div>
      </div>

      <div style={{ flex: 1, padding: 24 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Ring gauge card */}
          <div style={{ width: 340, flexShrink: 0, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-4)', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <div style={{ position: 'relative', width: 160, height: 160 }}>
              <svg width={160} height={160} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={80} cy={80} r={R} fill="none" stroke="var(--paper-3)" strokeWidth={12} />
                <circle
                  cx={80} cy={80} r={R}
                  fill="none"
                  stroke={danger ? 'var(--danger)' : 'var(--accent)'}
                  strokeWidth={12}
                  strokeDasharray={`${filled} ${C - filled}`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 0.5s ease' }}
                />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 'var(--fs-3xl)', fontWeight: 700, color: danger ? 'var(--danger)' : 'var(--ink)' }}>
                  {pct.toFixed(0)}%
                </span>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>used</span>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>
                {formatBytes(usedBytes)} of {formatBytes(quotaBytes)}
              </div>
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 2 }}>
                {formatBytes(availableBytes)} available
              </div>
            </div>

            {viewMode === 'workspace' && !isAdmin && (
              <Button kind="accent" block onClick={() => setShowRequest(true)} icon="plus">Request more storage</Button>
            )}
            {viewMode === 'workspace' && isAdmin && (
              <Button kind="accent" block onClick={() => navigate('/members')}>Manage allocations</Button>
            )}
            {viewMode === 'personal' && (
              <Button kind="accent" block icon="sparkle" onClick={() => navigate('/account')}>Upgrade plan</Button>
            )}
          </div>

          {/* Your files breakdown */}
          <div style={{ flex: 1, minWidth: 380, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-4)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--fs-lg)' }}>Your files</div>
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 2 }}>Browsable — click to open</div>
            </div>
            <div style={{ borderTop: '1px solid var(--line)' }}>
              {fileRows.map(row => (
                <div
                  key={row.id}
                  onClick={() => navigate('/browse', row.isRoot ? undefined : { state: { folderId: row.id } })}
                  style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--paper-2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Icon name="folder" size={16} color="var(--accent)" />
                    <span style={{ flex: 1, fontSize: 'var(--fs-md)', color: 'var(--ink)' }}>{row.name}</span>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{formatBytes(row.bytes)}</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 'var(--r-full)', background: 'var(--paper-3)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(row.bytes / maxRowBytes) * 100}%`, background: 'var(--accent)', borderRadius: 'var(--r-full)' }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: 16 }}>
              <Button kind="outline" icon="hdd" onClick={() => navigate('/browse')}>Open My Drive</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Request more storage modal */}
      <Modal
        open={showRequest}
        onClose={() => setShowRequest(false)}
        title="Request more storage"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowRequest(false)}>Cancel</Button>
            <Button kind="accent" loading={requestMutation.isPending} onClick={() => requestMutation.mutate()}>Submit request</Button>
          </>
        }
      >
        <div className="t-stack">
          <div className="t-field">
            <label>Additional storage</label>
            <select className="t-input" value={requestGB} onChange={e => setRequestGB(Number(e.target.value))}>
              <option value={10}>+10 GB</option>
              <option value={50}>+50 GB</option>
              <option value={100}>+100 GB</option>
              <option value={200}>+200 GB</option>
            </select>
          </div>
          <div className="t-field">
            <label>Reason</label>
            <textarea className="t-input" rows={3} placeholder="Explain why you need more storage…" value={requestReason} onChange={e => setRequestReason(e.target.value)} />
          </div>
          <div className="t-alert t-alert--info">
            <Icon name="info" size={13} />
            Your admin will review and approve this request.
          </div>
        </div>
      </Modal>
    </div>
  );
}
